import React, {createContext, FunctionComponent, useState, useEffect} from 'react';

import type {IProductStencilContext} from '../organisms/product-stencil/product-stencil.types';

/* --- PDP components --- */
import {ProductGallery} from '../organisms/product-stencil/overview/ProductGallery/ProductGallery';
import {ProductOverview} from '../organisms/product-stencil/overview/ProductOverview';
import {RelatedProducts} from '../organisms/product-stencil/RelatedProducts/RelatedProducts';
import {AlsoBought} from '../organisms/product-stencil/AlsoBought/AlsoBought';
import {RelatedRecipes} from '../organisms/product-stencil/RelatedRecipes/RelatedRecipes';
import {Breadcrumb} from '../organisms/product-stencil/Breadcrumb/Breadcrumb';
import {getProductPackaging} from "../../api/ECOM/getProductPackaging.api";
import {getConfig} from "../../api/ECOM/getConfig.api";
import {isLoggedIn} from "../../data/user.model";
import {getSAPProductDetails} from "../../api/ECOM/getSAPProductDetails.api";
import {StencilSkeleton} from "../atoms/effects/skeletons/product-stencil/StencilSkeleton";
import {ProductDetails} from "../organisms/product-stencil/ProductDetails/ProductDetails";
import {ProductDetailsInjector, ProductDetailsInjectorType} from '../organisms/product-stencil/ProductDetailsInjector/ProductDetailsInjector';
import { pdpGA4TrackGuestViewItem, pdpGA4TrackViewItem } from '../../analytics/ga4/events/gaEventsPDP';
import {ErrorPage} from "./ErrorPage";
import {DeliveryBanner} from "../organisms/product-stencil/DeliveryBanner/DeliveryBanner";
import {BasketOverlay} from "../organisms/product-stencil/BasketOverlay/BasketOverlay";
import {Products} from "../../interfaces/ecom/schema";
import {getBundles} from "../../api/ECOM/getBundles.api";

export const ProductContext = createContext<IProductStencilContext | null>(null);
export const TranslationsContext = createContext(null);

export const productPackagingRoot = document.getElementById(
    'react-product--packaging'
);

export const ProductStencil: FunctionComponent = () => {
    const [productData, setProductData] = useState(null);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        const scriptElement = document.getElementById('pdp__data');
        if (scriptElement) {
            const data = JSON.parse(scriptElement.textContent || '{}') as IProductStencilContext;
            setData(data);

            (async () => {
                try {
                    const cfPackagings = data.product?.packagings.filter(packaging => packaging.code !== null);
                    const config = isLoggedIn() && await getConfig();
                    const SapDetails = cfPackagings?.length ? await getSAPProductDetails(cfPackagings.map(({code}) => code)): [];
                    const isObsolete = SapDetails[0]?.IsObsolete;
                    const {Packaging} = isLoggedIn() && await getProductPackaging({AEMProductID: data.product.id, isObsolete});
                    const {PromotionStructure} = isLoggedIn() && await getBundles([data.product.id]);
                    const packagingData = Packaging?.length ? sortPackagingsByStockType (Packaging) : SapDetails;
                    const activeProductIndex = checkIfMaterialIsInQuery(packagingData) || 0;
                    const showBasketOverlay = false;

                    if (data.product?.packagings.length && Packaging?.length) {
                        data.product.packagings = sortCfPackagingsByOSPackagings(data.product.packagings, Packaging);
                    }

                    setProductData({
                        ...data,
                        Packaging: packagingData,
                        PromotionStructure: PromotionStructure || [],
                        activeProductIndex,
                        setActiveProduct,
                        config: config ? config : null,
                        SapDetails: SapDetails ? sortSapDetailsByPackagings(SapDetails, packagingData) : null,
                        quantityAddedToCart: 0,
                        showBasketOverlay,
                        setShowBasketOverlay,
                        alsoBoughtProducts: {Products: []},
                        setAlsoBoughtProducts,
                    });
                    setIsLoading(false);
                } catch (error) {
                    console.error('Caught error:', error);
                    setIsError(true);
                    setIsLoading(false);
                  }
            })()
        }
    }, []);

    const setActiveProduct = (materialNumber: string) => {
        setProductData((prevData: IProductStencilContext) => prevData ? {
            ...prevData,
            activeProductIndex: prevData.Packaging.findIndex(({MaterialNumber}) => (MaterialNumber === materialNumber))
        } : null);
    };

    const setShowBasketOverlay = (showBasketOverlay: boolean, quantity: number) => {
        setProductData((prevData: IProductStencilContext) => prevData ? {
            ...prevData,
            showBasketOverlay,
            quantityAddedToCart: quantity ? quantity : prevData.quantityAddedToCart
        } : null);
    };

    const setAlsoBoughtProducts = (products: Products) => {
        setProductData((prevData: IProductStencilContext) => prevData ? {
            ...prevData,
            alsoBoughtProducts: products
        } : null);
    }

    const checkIfMaterialIsInQuery = (Packaging) => {
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has('materialNumber')) {
            return 0;
        }
        const materialNumber = urlParams.get('materialNumber');
        let foundIindex = -1;
        if (Packaging[0]?.MaterialNumber) {
            foundIindex = Packaging.findIndex(({MaterialNumber}) => (MaterialNumber === materialNumber));
        } else {
            foundIindex = Packaging.findIndex(({code}) => (code === materialNumber));
        }
        return foundIindex < 0 ? 0 : foundIindex;
    };

    const sortPackagingsByStockType  = (Packaging) => {
        return Packaging.sort((a, b) => (a.StockType === "BLOCKSALE" ? 1 : 0) - (b.StockType === "BLOCKSALE" ? 1 : 0));
    }

    function sortCfPackagingsByOSPackagings(cfPackagings, osPackagings) {
        if (!cfPackagings || !osPackagings || !cfPackagings.length || !osPackagings.length) {
            return cfPackagings ? [...cfPackagings] : [];
        }

        const packagingOrderMap = new Map();
        osPackagings.forEach((item, index) => {
            packagingOrderMap.set(item.MaterialNumber, index);
        });

        const sortedCfPackagings = [...cfPackagings];
        sortedCfPackagings.sort((a, b) => {
            const indexA = packagingOrderMap.get(a.code);
            const indexB = packagingOrderMap.get(b.code);
            if (indexA === undefined && indexB === undefined) { return 0; }
            if (indexA === undefined) { return 1; }
            if (indexB === undefined) { return -1; }
            return indexA - indexB;
        });

        return sortedCfPackagings;
    }

    const sortSapDetailsByPackagings = (SapDetails, Packaging) => {
        if (!Packaging?.length) {
            return SapDetails;
        }
        const SapDetailsMap = new Map(SapDetails.map(({ MaterialNumber, ...rest }) => [MaterialNumber, { MaterialNumber, ...rest }]));
        return Packaging.map(({ MaterialNumber }) => SapDetailsMap.get(MaterialNumber)).filter(Boolean);
    };

    if (isLoading) {
        return <StencilSkeleton/>;
    }
    if (isError) {
        return <ErrorPage stencil={{
            errorMessage: data?.stencil?.errorMessage,
            refreshButtonLabel: data?.stencil?.refreshButtonLabel,
            redirectButtonLabel: data?.stencil?.redirectButtonLabel,
            redirectButtonLink: data?.stencil?.redirectButtonLink
        }} />;
    }

    try {
        if (productData && productData.Packaging) {
            if (!isLoggedIn()) {
                pdpGA4TrackGuestViewItem(productData);
            } else {
                pdpGA4TrackViewItem(productData);
            }
        }
    } catch (error) {
        console.error('Caught error:', error);
    }

    return (
        <React.Fragment>
            <ProductContext.Provider value={productData}>
                <DeliveryBanner />
                <BasketOverlay />
                {!productData.product.hideBreadcrumb && <Breadcrumb/>}
                <section className="pdp__overview-carousel">
                    <div className="pdp-section__container">
                        <ProductGallery/>
                        <ProductOverview/>
                    </div>
                </section>

                <section className={"pdp__product-details"}>
                    <div className="pdp-section__container">
                        <ProductDetails/>
                    </div>
                </section>
                <section className="pdp__related-products">
                    <RelatedProducts/>
                </section>

                <ProductDetailsInjector injectType={ProductDetailsInjectorType.WHY} />

                <section className="pdp__also-bought">
                    <AlsoBought/>
                </section>
                <section className="pdp__related-recipes">
                    <RelatedRecipes/>
                </section>

                <ProductDetailsInjector injectType={ProductDetailsInjectorType.APPENDIX} />

                <ProductDetailsInjector injectType={ProductDetailsInjectorType.CTA_BANNER} />

            </ProductContext.Provider>
        </React.Fragment>
    );
};

export default ProductStencil;
